import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShoppingCart, ChevronRight } from 'lucide-react';
import { ordersService } from '@services/orders.service';
import { cn, formatCurrency } from '@lib/utils';

// §11c: the green intensity ramp. An order is a journey, not a state. Every
// in-progress step used to be the SAME mint chip, so an operator scanning the
// queue could not tell "accepted" from "en route" without reading the label.
// The hue is held and the intensity climbs, so the ramp is legible at a glance
// and still readable for a colour-blind operator (it is a lightness ramp).
//
// `pending` stays WARNING on purpose: it is the one step that genuinely needs
// attention, because it is waiting on the merchant. Everything from `accepted`
// onward is healthy forward motion and must not wear an alarm colour.
//
// The ramp terminates into --state-success; it does not replace it.
const statusChip: Record<string, string> = {
  pending:         'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  accepted:        'bg-[var(--progress-1)] text-[var(--progress-1-fg)]',
  preparing:       'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  processing:      'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  ready_for_pickup:'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  assigned:        'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  en_route:        'bg-[var(--progress-4)] text-[var(--progress-4-fg)]',
  delivered:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  confirmed:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  completed:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  cancelled:       'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  declined:        'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  rejected:        'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  failed:          'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  refunded:        'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

export const OrdersListPage = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', page, statusFilter],
    queryFn: () => ordersService.list({ page, limit: 20, status: statusFilter || undefined }),
  });

  return (
    <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
      {/* Page header */}
      <div className="space-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
          Platform
        </p>
        <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
          Orders
        </h1>
        <p className="text-[14px] text-[var(--text-muted)]">Platform-wide order overview</p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="w-52 rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="preparing">Preparing</option>
          <option value="en_route">En Route</option>
          <option value="delivered">Delivered</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table card */}
      <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
        {/* Loading state */}
        {isLoading && (
          <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
            Loading orders...
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
            Failed to load orders.
          </div>
        )}

        {/* Populated state */}
        {data && (
          <table className="w-full">
            <thead className="bg-[var(--bg-base)]">
              <tr>
                <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Order
                </th>
                <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Branch
                </th>
                <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Type
                </th>
                <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Total
                </th>
                <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Date
                </th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {Array.isArray(data.data) && data.data.map((order) => (
                <tr key={order.id} className="hover:bg-[var(--bg-base)] transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <ShoppingCart
                        className="w-5 h-5 text-[var(--text-muted)]"
                        strokeWidth={1.5}
                      />
                      <span className="text-[14px] font-medium text-[var(--text-primary)]">
                        #{order.branchOrderNumber || order.id.slice(0, 8)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-primary)]">
                    {order.branchName ?? '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                        statusChip[order.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                      )}
                    >
                      {order.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[13px] text-[var(--text-muted)] capitalize">
                    {order.deliveryType ?? '—'}
                  </td>
                  <td className="px-6 py-4 text-[14px] font-medium text-[var(--text-primary)]">
                    {formatCurrency(order.totalAmount)}
                  </td>
                  <td className="px-6 py-4 text-[14px] text-[var(--text-muted)]">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/orders/${order.id}`}
                      className="w-8 h-8 rounded-full inline-flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                    </Link>
                  </td>
                </tr>
              ))}

              {/* Empty state: rendered inside table body to maintain layout */}
              {Array.isArray(data.data) && data.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]">
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
